import './EditBox.scss';

import classNames from 'classnames';
import { EditOutlined } from '@ant-design/icons';
import { ProcessingIcon } from './general';
import { ProcessingState } from '@hooks';
import { When } from 'react-if';

import React, { PropsWithChildren, useCallback, useState } from 'react';

export interface EditBoxProps {
	className?: string;
	disabled?: boolean;
	editIcon?: boolean;
	isEmpty?: boolean;
	state?: ProcessingState;
	isReadOnly?: boolean;
	isSalesforce?: boolean;
	isSpreadsheet?: boolean;
	onClick?: () => void;
	onBlur?: () => void;
	readOnlyHover?: boolean;
	selectionRef?: React.MutableRefObject<{blur: () => void, focus: () => void} | null>;
}

export const EditBox: React.FC<PropsWithChildren<EditBoxProps>> = ({
	children,
	className,
	editIcon,
	isEmpty,
	isReadOnly,
	isSalesforce,
	isSpreadsheet,
	onClick,
	onBlur,
	readOnlyHover,
	selectionRef,
	state
}) => {
	const [ isEditing, setIsEditing ] = useState(false);

	const componentClassNames = classNames('edit-box', className, {
		'editing': isEditing,
		'empty': isEmpty,
		'processing-error': state?.result?.success === false,
		'readonly': isReadOnly,
		'readonly-hover': readOnlyHover,
		'spreadsheet': isSpreadsheet,
		'validation-error': Boolean(state?.result?.message)
	});

	const handleOnClick = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (isReadOnly) {
			return;
		}

		setIsEditing(true);

		if (onClick) {
			onClick();
		}

		if (selectionRef?.current) {
			setTimeout(selectionRef.current.focus);
		}
	}, [ isReadOnly, onClick, selectionRef ]);

	const handleOnBlur = useCallback(() => {
		if (onBlur) {
			onBlur();
		}

		if (selectionRef?.current) {
			setTimeout(selectionRef.current.blur);
		}

		setIsEditing(false);
	}, [ onBlur, selectionRef ]);

	const showEditIcon = editIcon && !isReadOnly && !isEmpty && !isEditing && !state?.isProcessing;

	return (
		<div
			className={ componentClassNames }
			onBlur={ handleOnBlur }
			onClick={ handleOnClick }>
			<div className='content'>
				{ children }
				<div className='activity-indicators'>
					<When condition={ showEditIcon }>
						<EditOutlined className='edit-icon' />
					</When>
					<When condition={ Boolean(state?.isProcessing || state?.result) }>
						<ProcessingIcon
							isProcessing={ state?.isProcessing ?? false }
							isSalesforce={ isSalesforce }
							result={ state?.result } />
					</When>
				</div>
			</div>
		</div>
	);
};
